import merge from "lodash/merge";
import moment from "moment/moment";
import { getSavedFiltersTarget } from "PFApp/booking/parts/overview/booking_module_calendar/toolbar/toolbar.utils";
import {
  ActivityDataEntry,
  BookingDataEntry
} from "PFApp/booking/parts/overview/calendar/managers/booking_data_manager";
import { INITIAL_BOOKING_DRAG_AND_DROP_DATA } from "PFApp/booking/parts/overview/calendar/managers/booking_drag_and_drop_manager";
import {
  BookingModuleStoreContext,
  BookingModuleStoreType,
  INITIAL_MAX_CALENDAR_RANGE_MONTHS
} from "PFApp/booking/store";
import { BookingModuleDataController } from "PFApp/booking/store/provider/booking_module_data_controller";
import { getPartializedStoreToPersist } from "PFApp/booking/store/provider/get_partialized_store_to_persist";
import { CalendarMode, Tabs, WorkforceMetricsMode } from "PFApp/booking/types";
import { BookingsVisibility } from "PFApp/booking/types/bookings_visibility";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { isWorkforceProfiles } from "PFApp/booking/utils/calendar_root.utils";
import { useFiltersV2Actions } from "PFApp/components/filters_v2";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useDateFormatter } from "PFCore/hooks";
import { FeatureFlag } from "PFCore/types";
import { PropsWithChildren, useState } from "react";
import { useNavigate } from "react-router";
import { createStore } from "zustand";
import { persist } from "zustand/middleware";

import { useDefaultWorkforceMetricsMode } from "./use_default_workforce_metrics_mode";
import { useProjectsEngagementsStoreSlice } from "./use_projects_engagements_store_slice";
import { useProjectsStoreSlice } from "./use_projects_store_slice";
import { useWorkforceStoreSlice } from "./use_workforce_store_slice";

export const BookingModuleStoreProvider = ({ children }: PropsWithChildren) => {
  const { resetFilters, setFiltersCollection } = useFiltersV2Actions();
  const defaultWorkforceMetricsMode = useDefaultWorkforceMetricsMode();
  const { formatISODate } = useDateFormatter();
  const navigate = useNavigate();

  const showUtilizationMetricsEnabled = useIsFeatureEnabled()(FeatureFlag.ShowUtilization);

  const { createWorkforceStoreSlice, createWorkforceStoreSliceActions } = useWorkforceStoreSlice();
  const { createProjectsEngagementsStoreSlice, createProjectsEngagementsStoreSliceActions } =
    useProjectsEngagementsStoreSlice();
  const {
    createProjectsStoreSlice: createProjectsRolesStoreSlice,
    createProjectsStoreSliceActions: createProjectsRolesStoreSliceActions
  } = useProjectsStoreSlice({
    calendarRoot: CalendarRoot.ProjectsRoles
  });

  const [store] = useState(() =>
    createStore<BookingModuleStoreType>()(
      persist(
        (...args) => {
          const [set, get] = args;
          return {
            tab: Tabs.Overview,
            calendarMode: CalendarMode.Projects,
            calendarRootByMode: {
              [CalendarMode.Projects]: CalendarRoot.ProjectsEngagements,
              [CalendarMode.Workforce]: CalendarRoot.WorkforceProfiles
            },
            calendarRange: {
              min: formatISODate(),
              max: formatISODate(moment().add(INITIAL_MAX_CALENDAR_RANGE_MONTHS, "M"))
            },
            headerDisabled: false,
            workforceMetrics: {
              mode: defaultWorkforceMetricsMode,
              metrics: undefined,
              loading: false
            },
            tooltipVirtualBoundaryElement: null, // only change from null to DOM node is reactive. Non-serializable object won't trigger re-renders
            thresholds: {},
            loading: false,
            isMisalignmentsVisible: true,
            visibleRowsRange: null,
            rowHeightsByKeyCache: new Map(),
            bookingsData: {} as Record<number, BookingDataEntry>,
            auditActivitiesData: {} as Record<number, ActivityDataEntry>,
            bookingDragAndDropData: { ...INITIAL_BOOKING_DRAG_AND_DROP_DATA },
            isBMManagersEnabled: localStorage.getItem("bm_use_managers") === "true",
            [CalendarRoot.WorkforceProfiles]: createWorkforceStoreSlice(...args),
            [CalendarRoot.ProjectsEngagements]: createProjectsEngagementsStoreSlice(...args),
            [CalendarRoot.ProjectsRoles]: createProjectsRolesStoreSlice(...args),
            actions: {
              setTab: (tab) => set({ tab }),
              changeTab: (tab, initialTabData) => {
                navigate(`/booking/${tab}`); // here we don't need `search` as it is already transformed into initialTabData
                set({ tab });
                if (initialTabData) {
                  get().actions.setCalendarMode(initialTabData.calendarMode, initialTabData.data);
                }
              },
              setCalendarMode: (calendarMode, calendarModeInitialData = {}) => {
                const { filters, calendarRoot } = calendarModeInitialData;
                const { calendarRootByMode, actions } = get();
                const calendarRootToBeSet = calendarRoot ?? calendarRootByMode[calendarMode];
                set({ calendarMode });
                actions.setCalendarRootByMode(calendarMode, calendarRootToBeSet); // should reset the filters collection
                if (filters) {
                  resetFilters({ mainFilters: filters });
                  actions[calendarRootToBeSet].resetPagination();
                }
              },
              setCalendarRootByMode: (calendarMode, calendarRoot) => {
                const { calendarRootByMode } = get();
                set({
                  calendarRootByMode: { ...calendarRootByMode, [calendarMode]: calendarRoot },
                  rowHeightsByKeyCache: new Map()
                });
                const collection = getSavedFiltersTarget(calendarRoot);
                setFiltersCollection(collection);
              },
              setCalendarRange: (calendarRange) => set({ calendarRange }),
              setHeaderDisabled: (headerDisabled) => set({ headerDisabled }),
              setWorkforceMetrics: (workforceMetrics) => {
                const { workforceMetrics: prevWorkforceMetrics } = get();
                set({ workforceMetrics: { ...prevWorkforceMetrics, ...workforceMetrics } });
              },
              setTooltipVirtualBoundaryElement: (tooltipVirtualBoundaryElement) =>
                set({ tooltipVirtualBoundaryElement }),
              setThresholds: (thresholds) => set({ thresholds }),
              setLoading: (loading) => set({ loading }),
              setIsMisalignmentsVisible: (isMisalignmentsVisible) => set({ isMisalignmentsVisible }),
              setVisibleRowsRange: (visibleRowsRange) => set({ visibleRowsRange }),
              setRowHeightCache: (rowKey, height) => {
                // direct update to not trigger rerenders
                const state = get();
                state.rowHeightsByKeyCache.set(rowKey, height);
              },
              updateBookingsData: (bookingId, entry) => {
                const { bookingsData } = get();
                const existing = bookingsData[bookingId];
                set({
                  bookingsData: {
                    ...bookingsData,
                    [bookingId]: { ...existing, ...entry } as BookingDataEntry
                  }
                });
              },
              getBookingData: (bookingId) => get().bookingsData[bookingId],
              updateAuditActivitiesData: (activityId, entry) => {
                const { auditActivitiesData } = get();
                const existing = auditActivitiesData[activityId];
                set({
                  auditActivitiesData: {
                    ...auditActivitiesData,
                    [activityId]: { ...existing, ...entry } as ActivityDataEntry
                  }
                });
              },
              getAuditActivityData: (activityId) => get().auditActivitiesData[activityId],
              setBookingDragAndDropData: (data) => {
                const { bookingDragAndDropData } = get();
                set({
                  bookingDragAndDropData: {
                    ...bookingDragAndDropData,
                    ...data
                  }
                });
              },
              resetBookingDragAndDropData: () => {
                set({ bookingDragAndDropData: { ...INITIAL_BOOKING_DRAG_AND_DROP_DATA } });
              },
              getBookingDragAndDropData: () => get().bookingDragAndDropData,
              getCurrentRowsByKey: () => {
                const { calendarMode, calendarRootByMode } = get();
                const calendarRoot = calendarRootByMode[calendarMode];
                return get()[calendarRoot].rowsByKey;
              },
              getRowsVisibleByBookingsVisibility: () => {
                const { calendarMode, calendarRootByMode } = get();
                const calendarRoot = calendarRootByMode[calendarMode];
                const { rows } = get()[calendarRoot];
                if (!isWorkforceProfiles(calendarRoot)) {
                  return rows;
                }
                const { bookingsVisibility, bookingsVisibilityVisibleRowKeys } =
                  get()[CalendarRoot.WorkforceProfiles];
                return bookingsVisibility === BookingsVisibility.CurrentZoom &&
                  bookingsVisibilityVisibleRowKeys
                  ? rows.filter((row) => bookingsVisibilityVisibleRowKeys.has(row.key))
                  : rows;
              },
              [CalendarRoot.WorkforceProfiles]: createWorkforceStoreSliceActions(...args),
              [CalendarRoot.ProjectsEngagements]: createProjectsEngagementsStoreSliceActions(...args),
              [CalendarRoot.ProjectsRoles]: createProjectsRolesStoreSliceActions(...args)
            }
          };
        },
        {
          name: "booking-module-store",
          partialize: getPartializedStoreToPersist,
          merge: (persisted, current) => merge({}, current, persisted),
          onRehydrateStorage: () => (state) => {
            if (state) {
              const { calendarMode, calendarRootByMode } = state;
              const calendarRoot = calendarRootByMode[calendarMode];
              const collection = getSavedFiltersTarget(calendarRoot);
              setFiltersCollection(collection);

              const persistedMode = state.workforceMetrics.mode;

              if (
                !showUtilizationMetricsEnabled &&
                persistedMode !== WorkforceMetricsMode.TotalAvailability
              ) {
                state.actions.setWorkforceMetrics({ mode: WorkforceMetricsMode.TotalAvailability });
              }

              if (showUtilizationMetricsEnabled && persistedMode === WorkforceMetricsMode.TotalAvailability) {
                state.actions.setWorkforceMetrics({ mode: defaultWorkforceMetricsMode });
              }
            }
          }
        }
      )
    )
  );

  return (
    <BookingModuleStoreContext.Provider value={store}>
      <BookingModuleDataController>{children}</BookingModuleDataController>
    </BookingModuleStoreContext.Provider>
  );
};
